/**
 * Задачи: создание и подбор родителя.
 *
 * Иерархия в трекере ровно на три уровня, и её держит бэкенд: эпик наверху,
 * под ним история, задача или баг, под ними подзадача. Здесь она повторена
 * одной картой — не чтобы проверять вместо сервера, а чтобы не предлагать
 * человеку родителя, на котором форма всё равно получит отказ.
 */
import { issueApi } from '@/api'

import { emptyFilters, orNothing, type IssueFilters } from './filters'

import type { V1Issue, V1IssueType, V1Priority } from '@/api/clients/tracker-issue'

/** Кто чьим родителем бывает; типа нет в ключах — значит родителя не имеет */
const PARENT_TYPES: Partial<Record<V1IssueType, V1IssueType[]>> = {
  ISSUE_TYPE_STORY: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_TASK: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_BUG: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_SUBTASK: ['ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG'],
}

/** Приоритеты сверху вниз — тем же порядком их и выбирают в форме и в отборе */
export const PRIORITIES: V1Priority[] = [
  'PRIORITY_HIGHEST',
  'PRIORITY_HIGH',
  'PRIORITY_MEDIUM',
  'PRIORITY_LOW',
  'PRIORITY_LOWEST',
]

/** Какие типы задач есть в проекте — по его профилю */
export const typesOfProfile = (profile: string): V1IssueType[] =>
  profile === 'TYPE_PROFILE_SIMPLE'
    ? ['ISSUE_TYPE_TASK', 'ISSUE_TYPE_SUBTASK']
    : ['ISSUE_TYPE_EPIC', 'ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG', 'ISSUE_TYPE_SUBTASK']

export const parentTypesOf = (type: V1IssueType | ''): V1IssueType[] =>
  (type && PARENT_TYPES[type]) || []

/**
 * Кого заводят под задачей этого типа. Обратная сторона той же карты: у эпика
 * детьми бывают история, задача и баг — по кнопке заводим задачу, это привычный
 * случай; у истории, задачи и бага — подзадача; у подзадачи детей не бывает
 * вовсе, и пустая строка здесь означает именно это (DEV-1593).
 */
const CHILD_TYPE: Partial<Record<V1IssueType, V1IssueType>> = {
  ISSUE_TYPE_EPIC: 'ISSUE_TYPE_TASK',
  ISSUE_TYPE_STORY: 'ISSUE_TYPE_SUBTASK',
  ISSUE_TYPE_TASK: 'ISSUE_TYPE_SUBTASK',
  ISSUE_TYPE_BUG: 'ISSUE_TYPE_SUBTASK',
}

export const childTypeOf = (type: V1IssueType | ''): V1IssueType | '' =>
  (type && CHILD_TYPE[type]) || ''

export interface CreateIssuePayload {
  projectId: string
  title: string
  descriptionMd: string
  type: V1IssueType
  priority: V1Priority
  /** Колонка, в которой задача появится; пусто — первая открытая на доске */
  columnId?: string
  parentId?: string
  assigneeId?: string
  /** Срок в виде YYYY-MM-DD, как его отдаёт календарь дизайн-системы */
  dueDate?: string
  /** Кого редактор описания заметил в упоминаниях */
  mentionedUserIds?: string[]
}

export interface CreatedIssue {
  id: string
  key: string
  columnId: string
}

/**
 * Пустые поля не отправляем вовсе: `column_id`, `parent_id` и `assignee_id`
 * объявлены `optional string` с проверкой на uuid, и пустая строка её не
 * проходит — сервер ответит 400 вместо «поле не заполнено».
 */
export const createIssue = async (payload: CreateIssuePayload): Promise<CreatedIssue> => {
  const { data } = await issueApi.issueServiceCreateIssue({
    projectId: payload.projectId,
    title: payload.title,
    descriptionMd: payload.descriptionMd,
    type: payload.type,
    priority: payload.priority,
    ...(payload.columnId ? { columnId: payload.columnId } : {}),
    ...(payload.parentId ? { parentId: payload.parentId } : {}),
    ...(payload.assigneeId ? { assigneeId: payload.assigneeId } : {}),
    // Срок — день, а сервер ждёт момент времени: берём начало дня в поясе
    // человека, иначе «сегодня» в Ханое уезжает на вчера.
    ...(payload.dueDate ? { dueDate: new Date(`${payload.dueDate}T00:00:00`).toISOString() } : {}),
    ...(payload.mentionedUserIds?.length ? { mentionedUserIds: payload.mentionedUserIds } : {}),
  })

  return {
    id: data.issue?.id ?? '',
    key: data.issue?.key ?? '',
    columnId: data.issue?.columnId ?? '',
  }
}

/** Строка списка задач */
export interface IssueRow {
  id: string
  key: string
  title: string
  type: V1IssueType | ''
  priority: V1Priority | ''
  assigneeId: string
  assigneeName: string
  /** Момент времени от сервера; в таблице от него остаётся день */
  dueDate: string
  /** Колонка доски — она же статус задачи в списке */
  columnId: string
  /** Родитель в иерархии: по нему собирается дерево эпика */
  parentId: string
}

/** Экспортирован ради подзадач: они те же строки, только с фильтром по родителю */
export const toRow = (issue: V1Issue): IssueRow => ({
  id: issue.id ?? '',
  key: issue.key ?? '',
  title: issue.title ?? '',
  type: issue.type ?? '',
  priority: issue.priority ?? '',
  assigneeId: issue.assigneeId ?? '',
  assigneeName: issue.assigneeName ?? '',
  dueDate: issue.dueDate ?? '',
  columnId: issue.columnId ?? '',
  parentId: issue.parentId ?? '',
})

/** По чему список умеет сортировать — ровно то, что принимает сервер */
export type IssueSortField = 'key' | 'priority' | 'due_date' | 'created_at' | 'updated_at'

export interface IssueSort {
  field: IssueSortField
  desc: boolean
}

export interface IssuePage {
  issues: IssueRow[]
  /** Всего задач под отбором — из них таблица и считает страницы */
  total: number
}

export const listIssues = async (
  projectId: string,
  filters: IssueFilters = emptyFilters(),
  sort: IssueSort = { field: 'key', desc: false },
  page = 1,
  perPage = 25,
): Promise<IssuePage> => {
  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    orNothing(filters.assigneeIds),
    undefined,
    orNothing(filters.types),
    orNothing(filters.priorities),
    undefined,
    filters.search.trim() || undefined,
    undefined,
    undefined,
    undefined,
    sort.field,
    sort.desc,
    String(page),
    String(perPage),
  )

  return {
    issues: (data.issues ?? []).map(toRow),
    // Счётчики приходят строками: в protobuf это int64, а он не влезает в number.
    total: Number(data.pageInfo?.total ?? 0),
  }
}

/**
 * Перенос задачи. `beforeIssueId` — задача, над которой она встаёт; пусто
 * означает конец колонки, и это не то же самое, что «куда-нибудь»: без него
 * карточка после переноса уезжала бы вниз независимо от того, куда её положили.
 *
 * Конец колонки — это отсутствие поля, а не пустая строка: поле объявлено
 * `optional string` с проверкой на uuid, и пустая строка её не проходит —
 * сервер отвечает 400 на каждый перенос в конец.
 */
export const moveIssue = async (
  issueId: string,
  columnId: string,
  beforeIssueId?: string,
): Promise<void> => {
  await issueApi.issueServiceMoveIssue(
    issueId,
    beforeIssueId ? { columnId, beforeIssueId } : { columnId },
  )
}

/**
 * Смена исполнителя из списка. Снять исполнителя — это пустая строка: поле
 * `optional string` без проверки на uuid, и сервер понимает её как «никто».
 */
export const setAssignee = async (issueId: string, assigneeId: string): Promise<void> => {
  await issueApi.issueServiceUpdateIssue(issueId, { assigneeId })
}

export interface ParentCandidate {
  id: string
  key: string
  title: string
  type: V1IssueType | ''
}

/** Сколько кандидатов в родители подтягиваем: дальше человеку проще искать */
const PARENT_LIMIT = 100

/**
 * Кандидаты в родители — задачи подходящего типа в том же проекте. Список
 * приходит целиком и ищется на месте: он короткий, а запрос на каждую букву
 * ради сотни строк не окупается.
 */
export const listParentCandidates = async (
  projectId: string,
  types: V1IssueType[],
): Promise<ParentCandidate[]> => {
  if (!types.length) return []

  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    undefined,
    undefined,
    types,
    undefined,
    undefined,
    undefined,
    undefined,
    // Закрытые эпики в родители не предлагаем: заводить в них новую задачу —
    // это почти всегда промах.
    true,
    undefined,
    'key',
    false,
    '1',
    String(PARENT_LIMIT),
  )

  return (data.issues ?? []).map((issue) => ({
    id: issue.id ?? '',
    key: issue.key ?? '',
    title: issue.title ?? '',
    type: issue.type ?? '',
  }))
}
